import { defineStore } from 'pinia';
import { ref } from 'vue';
import { JP_GENERIC_CODE_SS } from '@/constants/jeopardy-codes.const';
import {
  IJeopardy,
  IReviewSummary,
  IServiceResponseError,
  ISitecoreItem,
  ISitecoreReviewSummaryItem,
} from '@/interfaces/entities';
import { SwitchToMonthlyService as _switchToMonthly } from '@/services/store/switch-to-monthly.service';
import {
  SwitchToMonthlyUpdateModel,
  SwitchToMonthlyPaymentsViewModel,
} from '@/interfaces/forms/switch-to-monthly/switch-to-monthly.interface';
import { QuoteSessionService as _quoteSessionService } from '@/services/store/quote-session.service';
import { InsuranceProduct } from '@/interfaces/entities/insurance-product.interface';
import { PaymentMethod, PaymentTypes } from '@/enums/payment-types.enum';
import { ICardDetails, IDirectDebitDetails, InsuranceQuoteType, QuoteTypeCodes } from '@/interfaces';
import { getCoverageDescriptionsByCoverageCode } from '@/utils/insurance/policy-helper';
import { replaceBookmarks } from '@/utils/bookmark.replace';
import { formatMaskedCardNumber } from '@/utils/text-transforms';
import { SSChangePaymentDetailsRules } from '@/utils/display-rules/ss-change-payment-details-rules';
import { toCurrency } from '@/utils/convert';
import { format } from 'date-fns';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { FormEvents } from '@/enums/form-events.enum';
import { IAnalyticsPaymentDetail, IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { useAnalyticsStore } from '@/store';
import { getPolicyDescription } from '@/utils/editable-steps/quote-content-builder';

export const useSwitchToMonthlyStore = defineStore('SwitchToMonthly', () => {
  const analyticsStore = useAnalyticsStore();
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const quoteSessionId = ref<string>();
  const errors = ref<IServiceResponseError[]>([]);
  const currentPolicy = ref<InsuranceProduct>();
  const switchToMonthlyPaymentsViewModel = ref<SwitchToMonthlyPaymentsViewModel>();
  const switchToMonthlyPaymentsViewModelInitialPrices = ref<SwitchToMonthlyPaymentsViewModel>();
  const jeopardies = ref<IJeopardy[]>([]);
  const model = ref<SwitchToMonthlyUpdateModel>();
  const paymentReceiptNumber = ref<string>();
  const isMotor = ref(true);
  const isHome = ref(false);
  const isPet = ref(false);
  const isBoat = ref(false);
  const isHealth = ref(false);

  /**
   * Get Policy
   */
  async function getPolicy() {
    const params = new URLSearchParams(location.search);
    quoteSessionId.value = params.get('quoteSessionId') ?? '';
    if (!quoteSessionId.value) throw JP_GENERIC_CODE_SS;
    const response = await _switchToMonthly.getPolicy(quoteSessionId.value);
    errors.value = response.errors;
    if (response.success && response.data) {
      currentPolicy.value = response.data;
      setInsuranceType(currentPolicy.value?.coverages[0]?.coverageCode);
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE_SS;
    }
  }

  /**
   * Apply Business Rules
   */
  async function applyBusinessRules(requestQuote?: boolean, bindChange?: boolean) {
    const response = await _switchToMonthly.applyBusinessRules({
      quoteSessionId: quoteSessionId.value,
      model: model.value,
      requestQuote: requestQuote,
      bindChange: bindChange,
      currentPolicy: currentPolicy.value,
    });
    errors.value = response.errors;
    if (response.success && response.data) {
      jeopardies.value = response.data.jeopardies;
      if (jeopardies.value && jeopardies.value.length > 0) {
        throw jeopardies.value[0].code;
      }
      switchToMonthlyPaymentsViewModel.value = response.data?.viewModel;
      if (bindChange != true) {
        switchToMonthlyPaymentsViewModelInitialPrices.value = switchToMonthlyPaymentsViewModel.value;
      }
    }
    if (errors.value && errors.value.length > 0) {
      throw errors.value[0].message ?? JP_GENERIC_CODE_SS;
    }
    if (!switchToMonthlyPaymentsViewModel.value) {
      throw JP_GENERIC_CODE_SS;
    }
  }

  /**
   * Saves payment details for summary
   */
  function savePaymentDetails(
    selectedPaymentMethod: PaymentTypes | null,
    cardDetails: ICardDetails | null,
    directDebitDetails: IDirectDebitDetails | null
  ) {
    model.value = {};
    if (PaymentTypes[selectedPaymentMethod ?? PaymentTypes.CreditCard] === PaymentMethod[PaymentMethod.CreditCard]) {
      model.value = {
        paymentMethod: PaymentMethod.CreditCard,
        creditCardDetails: {
          cardType: cardDetails?.cardType ?? '',
          maskedCardNumber: cardDetails?.maskedCardNumber ?? '',
          token: cardDetails?.token ?? '',
          cardHolder: cardDetails?.cardHoldersName ?? '',
          cardExpiry: `${cardDetails?.cardExpiryMonth}/${cardDetails?.cardExpiryYear}`,
        },
      };
    } else if (
      PaymentTypes[selectedPaymentMethod ?? PaymentTypes.BankAccount] === PaymentMethod[PaymentMethod.BankAccount]
    ) {
      model.value = {
        paymentMethod: PaymentMethod.BankAccount,
        bankAccountDetails: {
          bsb: directDebitDetails?.bankStateBranchNumber ?? '',
          accountNumber: directDebitDetails?.accountNumber ?? '',
          accountName: directDebitDetails?.nameOfAccount ?? '',
        },
      };
    }
  }

  /**
   * Generate policy details for display
   */
  function getPremiumDetails(instalmentFeesAdminField: any, infoBoxTextField: any = {}) {
    const viewModel = switchToMonthlyPaymentsViewModel.value;
    const initialPrices = switchToMonthlyPaymentsViewModelInitialPrices.value;
    if (!viewModel || !initialPrices) return;

    const adminFeeApplies = viewModel.instalmentFeesApply && viewModel.instalmentFeesPerMonth;
    const adminFeeText = adminFeeApplies ? instalmentFeesAdminField.value : '';

    let goodNewsTextApplies,
      goodNewsText = '';
    if (infoBoxTextField?.value) {
      goodNewsTextApplies = !viewModel.instalmentFeesApply && initialPrices.instalmentFeesPerMonth;
      goodNewsText = goodNewsTextApplies ? infoBoxTextField?.value : '';
    }

    return {
      monthlyNonResponsivePremium: viewModel.quotePremiumDetails?.monthlyNonResponsivePremium ?? 0,
      annualNonResponsivePremium: viewModel.quotePremiumDetails?.annualNonResponsivePremium,
      instalmentFeesPerMonth:
        viewModel.instalmentFeesPerMonth <= 0
          ? initialPrices?.instalmentFeesPerMonth
          : viewModel.instalmentFeesPerMonth,
      instalmentFeesPerYear:
        viewModel.instalmentFeesPerYear <= 0 ? initialPrices?.instalmentFeesPerYear : viewModel.instalmentFeesPerYear,
      adminFeeText: adminFeeText,
      instalmentFeesAdminField: adminFeeText,
      goodNewsText: goodNewsTextApplies
        ? replaceBookmarks(goodNewsText, {
            annualPremium: toCurrency(initialPrices.instalmentFeesPerYear) ?? '',
            adminFeeAnnual: toCurrency(initialPrices.instalmentFeesPerYear) ?? '',
            adminFee: toCurrency(initialPrices.instalmentFeesPerMonth) ?? '',
          })
        : '',
      infoBoxTextField: goodNewsText,
    };
  }

  /**
   * Generate a Policy Description and Coverage display name
   */
  function getProductSummary(dictionary: Record<string, string>) {
    return {
      policyDescription: currentPolicy.value?.coverages[0]
        ? getPolicyDescription(dictionary, currentPolicy.value?.coverages)
        : '',
      coverageDescriptions: getCoverages(),
    };
  }

  function getCoverages() {
    const formattedCoverages = currentPolicy.value?.coverages.map((coverage) => {
      return getCoverageDescriptionsByCoverageCode(coverage);
    });
    return formattedCoverages?.join(', ');
  }

  /**
   * Generate a review summary model for review and confirmation steps
   */
  function getReviewSummaryModel(summaryFields: any): IReviewSummary[] {
    const summary = {
      nameOnCard: model.value?.creditCardDetails?.cardHolder,
      cardNumber: formatMaskedCardNumber(model.value?.creditCardDetails?.maskedCardNumber ?? ''),
      cardExpiry: model.value?.creditCardDetails?.cardExpiry,
      nextPaymentDue: format(
        switchToMonthlyPaymentsViewModel.value?.quotePremiumDetails?.nonResponsiveNextInvoiceDue ?? '',
        STANDARD_DATE_FORMAT
      ),
      accountNumber: model.value?.bankAccountDetails?.accountNumber,
      accountName: model.value?.bankAccountDetails?.accountName,
      bsb: model.value?.bankAccountDetails?.bsb,
    };

    return mapPaymentDetails(summary, summaryFields);
  }

  function mapPaymentDetails(summary: Record<string, any>, summaryFields: any): IReviewSummary[] {
    const summaryFieldsReplaced = replaceBookmarks(summaryFields, summary);
    const reviewSummaries: IReviewSummary[] = summaryFieldsReplaced.map((field: any) => ({
      ...field,
      icon: {
        value:
          (model.value?.paymentMethod == PaymentMethod.BankAccount
            ? 'bg-icon-bank-account'
            : 'bg-payment-filled-icon') + ' summary-title-icon',
      },
      items: field.items
        .filter((item: ISitecoreReviewSummaryItem) => {
          return executeDisplayRules(item.fields.displayRules, model.value?.paymentMethod);
        })
        .map((item: ISitecoreReviewSummaryItem) => {
          return {
            ...item,
            ...item.fields,
          };
        }),
    }));

    return reviewSummaries;
  }

  function executeDisplayRules(rules: ISitecoreItem[], paymentMethod: PaymentMethod | null | undefined) {
    if (rules && rules.length > 0) {
      const anyRuleIsTrue = rules.some(function (rule) {
        if ((SSChangePaymentDetailsRules as any)[rule.name]) {
          return (SSChangePaymentDetailsRules as any)[rule.name](paymentMethod);
        }
      });
      return anyRuleIsTrue;
    }

    return true;
  }

  function setInsuranceType(coverageCode: string | null | undefined) {
    const coverage = currentPolicy.value?.coverages[0];

    isPet.value = coverage?.isPet ?? false;
    isHome.value = coverage?.isHome ?? false;
    isMotor.value = coverage?.isMotor ?? false;
    isBoat.value = coverage?.isBoat ?? false;
    isHealth.value = coverage?.isHealth ?? false;

    switch (coverageCode) {
      case QuoteTypeCodes[InsuranceQuoteType.Contents]:
      case QuoteTypeCodes[InsuranceQuoteType.Home]:
      case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents]:
        isHome.value = true;
        break;
      case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive]:
      case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability]:
      case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability]:
      case QuoteTypeCodes[InsuranceQuoteType.Caravan]:
      case QuoteTypeCodes[InsuranceQuoteType.Motorcycle]:
      case QuoteTypeCodes[InsuranceQuoteType.BoatComprehensive]:
      case QuoteTypeCodes[InsuranceQuoteType.BoatThirdParty]:
        isMotor.value = true;
        break;
      case QuoteTypeCodes[InsuranceQuoteType.PetComp]:
        isPet.value = true;
        break;
    }
  }

  /**
   * Withdraw Quote Session
   */
  async function withdrawQuoteSession() {
    const response = await _quoteSessionService.withdrawQuoteSession(quoteSessionId.value ?? '');
    errors.value = response.errors;
    if (response.errors && response.errors.length > 0) {
      throw response.errors[0].message ?? JP_GENERIC_CODE_SS;
    }
    return response;
  }

  /**
   * Bind Quote Session
   */
  async function bindQuoteSession() {
    const response = await _quoteSessionService.bindQuoteSession(quoteSessionId.value ?? '');
    errors.value = response.errors;
    if (response.errors && response.errors.length > 0) {
      throw response.errors[0].message ?? JP_GENERIC_CODE_SS;
    }
    return response;
  }

  /**
   * Push data to analytics
   *
   * @param options
   */
  const pushAnalytics = (options: {
    event: FormEvents;
    amount?: number;
    payment?: Partial<IAnalyticsPaymentDetail>;
    productName?: string;
  }) => {
    const firstVehicle = currentPolicy.value?.coverages?.at(0);
    const model: IFormAnalytics = {
      event: options.event,
      form: {
        detail: {
          makeName: firstVehicle?.vehicle?.make ?? '',
          modelName: firstVehicle?.vehicle?.model ?? '',
          vehicleYear: firstVehicle?.vehicle?.year ?? '',
        },
      },
    };
    model.form.termEndDate = format(currentPolicy.value?.termEnd ?? '', STANDARD_DATE_FORMAT);
    if (options.productName) {
      model.form.productName = options.productName;
    }
    if (options.amount) {
      model.form.amount = options.amount;
    }
    if (options.payment) {
      model.payment = options.payment;
    }

    analyticsStore.pushAnalytics(model);
  };
  const deleteQuoteSession = async () => {
    try {
      await _quoteSessionService.deleteQuoteSession(quoteSessionId.value ?? '');
    } catch (e) {
      //ignore if delete quote session doesn't work
      console.error('Error delete quote session', e);
    }
  };
  return {
    getPolicy,
    applyBusinessRules,
    savePaymentDetails,
    getPremiumDetails,
    getProductSummary,
    getReviewSummaryModel,
    withdrawQuoteSession,
    bindQuoteSession,
    pushAnalytics,
    currentPolicy,
    model,
    switchToMonthlyPaymentsViewModel,
    switchToMonthlyPaymentsViewModelInitialPrices,
    jeopardies,
    errors,
    paymentReceiptNumber,
    isMotor,
    isHome,
    isPet,
    isBoat,
    isHealth,
    deleteQuoteSession,
  };
});
